export interface ApiError {
  status: number;
  error: string;
  issues?: unknown;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  // Fastify 5 rejects POST/PATCH com content-type:application/json e body vazio
  // (FST_ERR_CTP_EMPTY_JSON_BODY). Só anexa o header quando realmente há body.
  const hasBody = init?.body !== undefined && init.body !== null;
  // FormData (upload multipart) precisa do content-type definido pelo browser,
  // COM o boundary — nunca forçar application/json nesses casos.
  const isFormData = typeof FormData !== 'undefined' && init?.body instanceof FormData;
  const headers: Record<string, string> = { ...(init?.headers as Record<string, string> | undefined ?? {}) };
  if (hasBody && !isFormData && !headers['content-type']) headers['content-type'] = 'application/json';

  const res = await fetch(path, {
    credentials: 'include',
    ...init,
    headers,
  });
  const text = await res.text();
  const data = text ? JSON.parse(text) : {};
  if (!res.ok) {
    const err: ApiError = { status: res.status, error: data.error ?? 'unknown', issues: data.issues };
    throw err;
  }
  return data as T;
}

// ---- NEO Pilots (fluxo não-código: onboarding + bloco Fundação) ----
export interface NeoTask {
  id: string;
  skillSlug: string;
  status: 'blocked' | 'todo' | 'running' | 'review' | 'done' | 'rollback';
  parentId: string | null;
  outputPath: string | null;
  error: string | null;
}
export interface NeoOnboardingItem { questionKey: string; question: string; answer: string; order: number }

export interface NeoPlanStep {
  id: string;
  order: number;
  title: string;
  description: string;
  kind: 'content' | 'agent' | 'code';
  engine: 'hermes' | 'claude' | null;
  spec: Record<string, unknown>;
  status: 'proposed' | 'approved' | 'running' | 'done' | 'failed' | 'skipped';
  result: Record<string, unknown> | null;
  outputPath: string | null;
  error: string | null;
}
export type NeoStepPatch = Partial<Pick<NeoPlanStep, 'title' | 'description' | 'kind' | 'engine' | 'spec' | 'order'>> & { status?: 'proposed' | 'approved' | 'skipped' };

export interface NeoRoutine {
  id: string;
  kind: 'editorial';
  active: boolean;
  hourUtc: number;
  lastRunAt: string | null;
  lastResult: Record<string, unknown> | null;
}

export interface NeoOnbQuestion { questionKey: string; question: string; hint?: string }
export interface NeoOnbTemplate {
  id: string;
  icon: string;
  name: string;
  desc: string;
  required: number;
  questions: NeoOnbQuestion[];
}

export const neoApi = {
  createProject: (name: string) =>
    request<{ project: { id: string; slug: string; name: string } }>(
      '/v1/neo/projects',
      { method: 'POST', body: JSON.stringify({ name }) },
    ),
  saveOnboarding: (slug: string, responses: NeoOnboardingItem[]) =>
    request<{ ok: boolean; count: number }>(
      `/v1/projects/${encodeURIComponent(slug)}/neo/onboarding`,
      { method: 'POST', body: JSON.stringify({ responses }) },
    ),
  uploadDocs: (slug: string, files: File[]) => {
    const fd = new FormData();
    for (const f of files) fd.append('file', f);
    return request<{ ok: boolean; saved: string[] }>(
      `/v1/projects/${encodeURIComponent(slug)}/neo/upload`,
      { method: 'POST', body: fd },
    );
  },
  onboardingTemplates: () => request<{ templates: NeoOnbTemplate[] }>('/v1/neo/onboarding-templates'),
  saveOnboardingTemplates: (templates: NeoOnbTemplate[]) =>
    request<{ ok: boolean; templates: NeoOnbTemplate[] }>('/v1/neo/onboarding-templates', {
      method: 'PUT',
      body: JSON.stringify({ templates }),
    }),
  runFundacao: (slug: string) =>
    request<{ ok: boolean; started: boolean }>(
      `/v1/projects/${encodeURIComponent(slug)}/neo/fundacao`,
      { method: 'POST' },
    ),
  kanban: (slug: string) =>
    request<{ tasks: NeoTask[] }>(`/v1/projects/${encodeURIComponent(slug)}/neo/kanban`),
  runBlock: (slug: string, block: string) =>
    request<{ ok: boolean; started: boolean; block: string; created: number }>(
      `/v1/projects/${encodeURIComponent(slug)}/neo/block/${encodeURIComponent(block)}`,
      { method: 'POST' },
    ),
  blocks: () =>
    request<{ blocks: { key: string; label: string; skills: string[] }[] }>('/v1/neo/blocks'),
  chat: (slug: string, message: string, history: { role: 'user' | 'assistant'; content: string }[]) =>
    request<{ reply: string }>(
      `/v1/projects/${encodeURIComponent(slug)}/neo/chat`,
      { method: 'POST', body: JSON.stringify({ message, history }) },
    ),
  // Plano de execução (Planejador genérico).
  plan: (slug: string) =>
    request<{ steps: NeoPlanStep[] }>(`/v1/projects/${encodeURIComponent(slug)}/neo/plan`),
  generatePlan: (slug: string) =>
    request<{ ok: boolean; count: number; steps: NeoPlanStep[] }>(
      `/v1/projects/${encodeURIComponent(slug)}/neo/plan/generate`,
      { method: 'POST' },
    ),
  updateStep: (slug: string, id: string, patch: NeoStepPatch) =>
    request<{ step: NeoPlanStep }>(
      `/v1/projects/${encodeURIComponent(slug)}/neo/plan/steps/${id}`,
      { method: 'PATCH', body: JSON.stringify(patch) },
    ),
  runStep: (slug: string, id: string) =>
    request<{ step: NeoPlanStep }>(
      `/v1/projects/${encodeURIComponent(slug)}/neo/plan/steps/${id}/run`,
      { method: 'POST' },
    ),
  createStep: (slug: string, body: { title: string; description?: string; kind: 'content' | 'agent' | 'code'; spec?: Record<string, unknown> }) =>
    request<{ step: NeoPlanStep }>(
      `/v1/projects/${encodeURIComponent(slug)}/neo/plan/steps`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  deleteStep: (slug: string, id: string) =>
    request<{ ok: boolean }>(
      `/v1/projects/${encodeURIComponent(slug)}/neo/plan/steps/${id}`,
      { method: 'DELETE' },
    ),
  // Padrões de agente (admin) — bases compostas em todo agente.
  agentBases: () =>
    request<{ bases: { name: string; title: string; guardrail: boolean }[] }>('/v1/neo/agent-bases'),
  agentBase: (name: string) =>
    request<{ name: string; content: string }>(`/v1/neo/agent-bases/${encodeURIComponent(name)}`),
  saveAgentBase: (name: string, content: string) =>
    request<{ ok: boolean }>(`/v1/neo/agent-bases/${encodeURIComponent(name)}`, {
      method: 'PUT',
      body: JSON.stringify({ content }),
    }),
  // Rotina de publicação automática.
  routine: (slug: string) =>
    request<{ routine: NeoRoutine | null }>(`/v1/projects/${encodeURIComponent(slug)}/neo/routine`),
  setRoutine: (slug: string, patch: { active?: boolean; hourUtc?: number }) =>
    request<{ routine: NeoRoutine | null }>(`/v1/projects/${encodeURIComponent(slug)}/neo/routine`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
  runRoutineNow: (slug: string) =>
    request<{ ok: boolean; started: boolean }>(
      `/v1/projects/${encodeURIComponent(slug)}/neo/routine/run-now`,
      { method: 'POST' },
    ),
  // Vitrine de artefatos (entregáveis) do projeto.
  artifacts: (slug: string) =>
    request<{
      docs: { file: string; title: string }[];
      site: string | null;
      publications: number;
      agents: { id: string; name: string; description: string }[];
    }>(`/v1/projects/${encodeURIComponent(slug)}/neo/artifacts`),
};

/** URL (mesma origem) do WebSocket de um console — o cookie de sessão vai no handshake. */
export function consoleWsUrl(id: string): string {
  const proto = location.protocol === 'https:' ? 'wss' : 'ws';
  return `${proto}://${location.host}/v1/console/sessions/${encodeURIComponent(id)}/ws`;
}

export interface ConsoleSessionMeta {
  id: string;
  kind: 'logs' | 'shell';
  target: string;
  title: string;
  interactive: boolean;
  ownerName: string;
  viewers: number;
  createdAt: string;
  closed: boolean;
}

export interface ConsoleContainer {
  name: string;
  image: string;
  status: string;
}

export interface UsageReport {
  days: number;
  totals: { costUsd: number; inputTokens: number; outputTokens: number; runs: number };
  byModel: Array<{ model: string; costUsd: number; inputTokens: number; outputTokens: number; runs: number }>;
  byKind: Array<{ kind: string; costUsd: number; runs: number }>;
  byDay: Array<{ day: string; costUsd: number }>;
}

export interface AppNotification {
  id: string;
  projectId: string | null;
  type: string;
  title: string;
  body: string | null;
  link: string | null;
  readAt: string | null;
  createdAt: string;
}

/** URL (mesma origem) que faz stream do avatar pela API. v = cache-buster. */
export function agentAvatarRawSrc(slug: string, agentId: string, v?: string | null): string {
  const q = v ? `?v=${encodeURIComponent(v)}` : '';
  return `/v1/projects/${encodeURIComponent(slug)}/agents/${agentId}/avatar/raw${q}`;
}

function docsBase(slug: string): string {
  return `/v1/projects/${encodeURIComponent(slug)}/docs`;
}
function encPath(path: string): string {
  return path
    .replace(/^docs\//, '')
    .split('/')
    .filter(Boolean)
    .map(encodeURIComponent)
    .join('/');
}

/** Igual a encPath, mas pra a biblioteca GLOBAL de skills (ancorada em skills/). */
function encSkillPath(path: string): string {
  return path
    .replace(/^skills\//, '')
    .split('/')
    .filter(Boolean)
    .map(encodeURIComponent)
    .join('/');
}

export const api = {
  login: (email: string, password: string) =>
    request<{ user: Me }>('/v1/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
  logout: () => request<{ ok: true }>('/v1/auth/logout', { method: 'POST' }),
  me: () => request<{ user: Me }>('/v1/auth/me'),

  // ---- App de sincronização: instaladores disponíveis pra download ----
  listSyncAppReleases: () =>
    request<{ files: SyncAppFile[] }>('/v1/sync-app/releases'),

  // ---- Personal Access Tokens (usados pelo app de sincronização de docs) ----
  listAccessTokens: () => request<{ tokens: AccessTokenInfo[] }>('/v1/account/tokens'),
  createAccessToken: (input: { label: string; expiresInDays?: number }) =>
    request<{ token: string; meta: AccessTokenInfo }>('/v1/account/tokens', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  revokeAccessToken: (id: string) =>
    request<{ ok: true }>(`/v1/account/tokens/${id}`, { method: 'DELETE' }),

  // ---- Device flow (autorizar o app de sync pelo site) ----
  deviceLookup: (code: string) =>
    request<{ device: DeviceLookupInfo }>(`/v1/device/lookup?code=${encodeURIComponent(code)}`),
  deviceApprove: (userCode: string) =>
    request<{ ok: true }>('/v1/device/approve', { method: 'POST', body: JSON.stringify({ userCode }) }),
  deviceDeny: (userCode: string) =>
    request<{ ok: true }>('/v1/device/deny', { method: 'POST', body: JSON.stringify({ userCode }) }),

  // ---- Notificações in-app ----
  listNotifications: () => request<{ notifications: AppNotification[]; unread: number }>('/v1/notifications'),
  markNotificationRead: (id: string) => request<{ ok: true }>(`/v1/notifications/${id}/read`, { method: 'POST' }),
  markAllNotificationsRead: () => request<{ ok: true }>('/v1/notifications/read-all', { method: 'POST' }),

  // ---- Ops (admin): execuções ativas + sessões + uso/custo ----
  getUsage: (days = 7) => request<UsageReport>(`/v1/admin/usage?days=${days}`),
  listRuns: () => request<{ runs: AdminRun[]; slots: RunSlots; serverNow: number }>('/v1/admin/runs'),
  killRun: (id: string) => request<{ ok: boolean }>(`/v1/admin/runs/${id}/kill`, { method: 'POST' }),
  listSessions: () =>
    request<{ sessions: WarmSession[]; ttlHours: number; serverNow: number }>('/v1/admin/sessions'),
  expireSession: (threadId: string) =>
    request<{ ok: true }>(`/v1/admin/sessions/${threadId}/expire`, { method: 'POST' }),

  // ---- Console / terminal ao vivo ----
  listConsoleSessions: () => request<{ sessions: ConsoleSessionMeta[] }>('/v1/console/sessions'),
  listConsoleContainers: () =>
    request<{ containers: ConsoleContainer[] }>('/v1/console/containers'),
  createConsoleSession: (input: { kind: 'logs' | 'shell'; target: string; title?: string }) =>
    request<{ session: ConsoleSessionMeta }>('/v1/console/sessions', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  closeConsoleSession: (id: string) =>
    request<{ ok: true }>(`/v1/console/sessions/${id}`, { method: 'DELETE' }),

  // ---- Código (viewer read-only) ----
  codeBranches: (slug: string) =>
    request<{ branches: string[]; baseBranch: string }>(`/v1/projects/${encodeURIComponent(slug)}/code/branches`),
  codeTree: (slug: string, ref?: string) =>
    request<{ ref: string; files: string[]; baseBranch: string }>(
      `/v1/projects/${encodeURIComponent(slug)}/code/tree${ref ? `?ref=${encodeURIComponent(ref)}` : ''}`,
    ),
  codeFile: (slug: string, path: string, ref?: string) =>
    request<{ path: string; ref: string; content: string }>(
      `/v1/projects/${encodeURIComponent(slug)}/code/file?path=${encodeURIComponent(path)}${ref ? `&ref=${encodeURIComponent(ref)}` : ''}`,
    ),
  codeDiff: (slug: string, head: string, base?: string) =>
    request<{ base: string; head: string; diff: string }>(
      `/v1/projects/${encodeURIComponent(slug)}/code/diff?head=${encodeURIComponent(head)}${base ? `&base=${encodeURIComponent(base)}` : ''}`,
    ),
  reportDiff: (slug: string, reportId: string) =>
    request<{ base: string; head: string; diff: string }>(
      `/v1/projects/${encodeURIComponent(slug)}/reports/${reportId}/diff`,
    ),

  // ---- Users (admin) ----
  listUsers: () => request<{ users: AdminUser[] }>('/v1/users'),
  createUser: (input: { email: string; displayName: string; password: string; globalRole: GlobalRole }) =>
    request<{ user: AdminUser }>('/v1/users', { method: 'POST', body: JSON.stringify(input) }),
  updateUser: (
    id: string,
    patch: { displayName?: string; globalRole?: GlobalRole; active?: boolean; password?: string },
  ) => request<{ user: AdminUser }>(`/v1/users/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),

  listProjects: () => request<{ projects: ProjectListItem[] }>('/v1/projects'),
  getProject: (slug: string) => request<{ project: Project }>(`/v1/projects/${encodeURIComponent(slug)}`),
  createProject: (input: NewProjectInput) =>
    request<{ project: Project }>('/v1/projects', { method: 'POST', body: JSON.stringify(input) }),
  updateProject: (slug: string, patch: ProjectPatch) =>
    request<{ project: Project }>(`/v1/projects/${encodeURIComponent(slug)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
  deleteProject: (slug: string) =>
    request<{ ok: boolean }>(`/v1/projects/${encodeURIComponent(slug)}`, { method: 'DELETE' }),

  listReports: (slug: string) =>
    request<{ reports: Report[] }>(`/v1/projects/${encodeURIComponent(slug)}/reports?limit=200`),
  createReport: (
    slug: string,
    input: {
      kind: 'bug' | 'suggestion';
      title: string;
      description: string;
      pageUrl?: string;
      releaseVersion?: string;
    },
  ) =>
    request<{ reportId: string; report: Report }>(
      `/v1/projects/${encodeURIComponent(slug)}/reports`,
      { method: 'POST', body: JSON.stringify(input) },
    ),
  getReport: (slug: string, reportId: string) =>
    request<{ report: Report; events: ReportEvent[] }>(
      `/v1/projects/${encodeURIComponent(slug)}/reports/${reportId}`,
    ),
  updateStatus: (slug: string, reportId: string, status: ReportStatus, note?: string) =>
    request<{ report: Report }>(
      `/v1/projects/${encodeURIComponent(slug)}/reports/${reportId}/status`,
      { method: 'POST', body: JSON.stringify({ status, note }) },
    ),
  // Abre o PR de um report já commitado (pós-validação no efêmero).
  openReportPr: (slug: string, reportId: string) =>
    request<{ ok: true }>(
      `/v1/projects/${encodeURIComponent(slug)}/reports/${reportId}/open-pr`,
      { method: 'POST' },
    ),
  deploy: (slug: string, reportId: string, target: 'dev' | 'prod' = 'dev') =>
    request<{ report: Report }>(
      `/v1/projects/${encodeURIComponent(slug)}/reports/${reportId}/deploy`,
      { method: 'POST', body: JSON.stringify({ target }) },
    ),
  testerFeedback: (slug: string, reportId: string, ok: boolean, note?: string) =>
    request<{ report: Report }>(
      `/v1/projects/${encodeURIComponent(slug)}/reports/${reportId}/tester-feedback`,
      { method: 'POST', body: JSON.stringify({ ok, note }) },
    ),
  artifactUrl: (slug: string, reportId: string, which: 'session' | 'console') =>
    request<{ url: string; expiresIn: number }>(
      `/v1/projects/${encodeURIComponent(slug)}/reports/${reportId}/artifacts/${which}`,
    ),

  listKeys: (slug: string) =>
    request<{ keys: Array<{ id: string; label: string; active: boolean; masked: string; createdAt: string; revokedAt: string | null }> }>(
      `/v1/projects/${encodeURIComponent(slug)}/keys`,
    ),
  issueKey: (slug: string, label: string) =>
    request<{ key: { id: string; publicKey: string; label: string } }>(
      `/v1/projects/${encodeURIComponent(slug)}/keys`,
      { method: 'POST', body: JSON.stringify({ label }) },
    ),
  revokeKey: (slug: string, keyId: string) =>
    request<{ ok: true }>(`/v1/projects/${encodeURIComponent(slug)}/keys/${keyId}/revoke`, { method: 'POST' }),

  // ---- Environments ----
  listEnvironments: (slug: string) =>
    request<{ environments: ProjectEnvironment[] }>(
      `/v1/projects/${encodeURIComponent(slug)}/environments`,
    ),
  createEnvironment: (slug: string, input: EnvironmentInput) =>
    request<{ environment: ProjectEnvironment }>(
      `/v1/projects/${encodeURIComponent(slug)}/environments`,
      { method: 'POST', body: JSON.stringify(input) },
    ),
  updateEnvironment: (slug: string, envId: string, patch: Partial<EnvironmentInput>) =>
    request<{ environment: ProjectEnvironment }>(
      `/v1/projects/${encodeURIComponent(slug)}/environments/${envId}`,
      { method: 'PATCH', body: JSON.stringify(patch) },
    ),
  deleteEnvironment: (slug: string, envId: string) =>
    request<{ ok: true }>(
      `/v1/projects/${encodeURIComponent(slug)}/environments/${envId}`,
      { method: 'DELETE' },
    ),

  // ---- Acesso a banco por agente (broker) ----
  dbQuery: (slug: string, input: { agentId: string; environmentId: string; query: string; confirm?: boolean }) =>
    request<DbQueryResult>(`/v1/projects/${encodeURIComponent(slug)}/db-query`, { method: 'POST', body: JSON.stringify(input) }),
  listAgentDbGrants: (slug: string, agentId: string) =>
    request<{ environments: ProjectEnvironment[]; grants: AgentDbGrant[] }>(
      `/v1/projects/${encodeURIComponent(slug)}/agents/${agentId}/db-grants`,
    ),
  saveAgentDbGrant: (slug: string, agentId: string, envId: string, body: Omit<AgentDbGrant, 'agentId' | 'environmentId'>) =>
    request<{ grant: AgentDbGrant }>(`/v1/projects/${encodeURIComponent(slug)}/agents/${agentId}/db-grants/${envId}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  deleteAgentDbGrant: (slug: string, agentId: string, envId: string) =>
    request<{ ok: true }>(`/v1/projects/${encodeURIComponent(slug)}/agents/${agentId}/db-grants/${envId}`, { method: 'DELETE' }),
  getDbAudit: (limit = 100) => request<{ audit: DbAuditRow[] }>(`/v1/admin/db-audit?limit=${limit}`),
  setupEnvRoles: (slug: string, envId: string, body: { superuser?: string; createWrite?: boolean }) =>
    request<{ ok: true; output: string; dbRoleRead: string; dbRoleWrite: string | null }>(
      `/v1/projects/${encodeURIComponent(slug)}/environments/${envId}/db-setup-roles`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  inspectEnvironment: (slug: string, envId: string) =>
    request<{ snapshot: EnvironmentSnapshot }>(
      `/v1/projects/${encodeURIComponent(slug)}/environments/${envId}/inspect`,
      { method: 'POST' },
    ),

  // ---- Test reports ----
  listTestReportBranches: (slug: string) =>
    request<{ branches: Array<{ branch: string; runs: number; lastRunAt: string }> }>(
      `/v1/projects/${encodeURIComponent(slug)}/test-reports/branches`,
    ),
  listTestReports: (slug: string, branch?: string, limit = 50) => {
    const qs = new URLSearchParams();
    if (branch) qs.set('branch', branch);
    qs.set('limit', String(limit));
    return request<{ reports: TestReport[] }>(
      `/v1/projects/${encodeURIComponent(slug)}/test-reports?${qs.toString()}`,
    );
  },
  importLocalTestReport: (
    slug: string,
    input: { resultsDir?: string; branch?: string } = {},
  ) =>
    request<ImportLocalTestReportResult>(
      `/v1/projects/${encodeURIComponent(slug)}/test-reports/import-local`,
      { method: 'POST', body: JSON.stringify(input) },
    ),

  // ---- Scenarios (BDD) ----
  listScenarios: (slug: string) =>
    request<{ scenarios: Report[] }>(
      `/v1/projects/${encodeURIComponent(slug)}/scenarios?limit=200`,
    ),
  listLocalFeatures: (slug: string) =>
    request<LocalFeaturesResponse>(
      `/v1/projects/${encodeURIComponent(slug)}/scenarios/local-features`,
    ),
  createScenario: (slug: string, input: { gherkin: string; title?: string }) =>
    request<{ report: Report }>(
      `/v1/projects/${encodeURIComponent(slug)}/scenarios`,
      { method: 'POST', body: JSON.stringify(input) },
    ),

  // ---- Step library ----
  listSteps: (slug: string) =>
    request<{ steps: StepLibraryEntry[] }>(`/v1/projects/${encodeURIComponent(slug)}/steps`),
  reindexSteps: (slug: string) =>
    request<{
      ok: true;
      filesScanned: number;
      stepsFound: number;
      inserted: number;
      updated: number;
      removed: number;
      scannedDirs: string[];
    }>(
      `/v1/projects/${encodeURIComponent(slug)}/steps/reindex`,
      { method: 'POST' },
    ),

  // ---- Agents (personas de conhecimento) ----
  listAgents: (slug: string) =>
    request<{ agents: Agent[] }>(`/v1/projects/${encodeURIComponent(slug)}/agents`),
  authProviders: () => request<{ google: boolean; hermes: boolean }>('/v1/auth/providers'),
  getAgent: (slug: string, agentId: string) =>
    request<{ agent: Agent; soul: string; writes: string[] }>(`/v1/projects/${encodeURIComponent(slug)}/agents/${agentId}`),
  createAgent: (slug: string, input: AgentInput) =>
    request<{ agent: Agent }>(`/v1/projects/${encodeURIComponent(slug)}/agents`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  updateAgent: (slug: string, agentId: string, patch: AgentPatch) =>
    request<{ agent: Agent }>(`/v1/projects/${encodeURIComponent(slug)}/agents/${agentId}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
  deleteAgent: (slug: string, agentId: string) =>
    request<{ ok: true }>(`/v1/projects/${encodeURIComponent(slug)}/agents/${agentId}`, { method: 'DELETE' }),
  generateSoul: (slug: string, agentId: string, input: { name: string; description: string; extra?: string }) =>
    request<{ soul: string }>(`/v1/projects/${encodeURIComponent(slug)}/agents/${agentId}/generate-soul`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  agentAvatarUrl: (slug: string, agentId: string) =>
    request<{ url: string; expiresIn: number }>(
      `/v1/projects/${encodeURIComponent(slug)}/agents/${agentId}/avatar`,
    ),
  generateAgentAvatar: (slug: string, agentId: string) =>
    request<{ agent: Agent; url: string }>(
      `/v1/projects/${encodeURIComponent(slug)}/agents/${agentId}/generate-avatar`,
      { method: 'POST' },
    ),
  listModels: () => request<{ models: ClaudeModel[] }>('/v1/models'),
  getFlowInstructions: (slug: string, agentId: string) =>
    request<{ flowInstructions: Array<{ flow: PipelineFlow; instructions: string; model: string | null }> }>(
      `/v1/projects/${encodeURIComponent(slug)}/agents/${agentId}/flow-instructions`,
    ),
  saveFlowInstruction: (slug: string, agentId: string, flow: PipelineFlow, input: { instructions?: string; model?: string | null }) =>
    request<{ ok: true }>(
      `/v1/projects/${encodeURIComponent(slug)}/agents/${agentId}/flow-instructions/${flow}`,
      { method: 'PUT', body: JSON.stringify(input) },
    ),
  uploadAgentAvatar: (slug: string, agentId: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return request<{ agent: Agent }>(
      `/v1/projects/${encodeURIComponent(slug)}/agents/${agentId}/avatar`,
      { method: 'POST', body: form },
    );
  },

  // ---- Ambientes efêmeros ----
  getEphemeralConfig: (slug: string) =>
    request<{ config: EphemeralConfig | null }>(`/v1/projects/${encodeURIComponent(slug)}/ephemeral/config`),
  saveEphemeralConfig: (slug: string, input: Partial<EphemeralConfigInput>) =>
    request<{ config: EphemeralConfig }>(`/v1/projects/${encodeURIComponent(slug)}/ephemeral/config`, {
      method: 'PUT',
      body: JSON.stringify(input),
    }),
  listEphemeral: (slug: string) =>
    request<{ environments: EphemeralEnv[] }>(`/v1/projects/${encodeURIComponent(slug)}/ephemeral`),
  createEphemeral: (slug: string, input: { branch: string; reportId?: string | null; mode?: 'auto' | 'manual'; ttlMinutes?: number | undefined }) =>
    request<{ environment: EphemeralEnv }>(`/v1/projects/${encodeURIComponent(slug)}/ephemeral`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  destroyEphemeral: (slug: string, id: string) =>
    request<{ ok: true }>(`/v1/projects/${encodeURIComponent(slug)}/ephemeral/${id}`, { method: 'DELETE' }),
  // Atualiza no lugar: puxa o commit novo da branch e rebuilda, mantendo a mesma URL.
  redeployEphemeral: (slug: string, id: string) =>
    request<{ ok: true }>(`/v1/projects/${encodeURIComponent(slug)}/ephemeral/${id}/redeploy`, { method: 'POST' }),

  // ---- Logs de aplicações ----
  listLogSources: (slug: string) =>
    request<{ sources: LogSource[]; defaultTtlHours: number }>(`/v1/projects/${encodeURIComponent(slug)}/logs/sources`),
  createLogSource: (slug: string, input: LogSourceInput) =>
    request<{ source: LogSource }>(`/v1/projects/${encodeURIComponent(slug)}/logs/sources`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  updateLogSource: (slug: string, id: string, patch: Partial<LogSourceInput>) =>
    request<{ source: LogSource }>(`/v1/projects/${encodeURIComponent(slug)}/logs/sources/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
  deleteLogSource: (slug: string, id: string) =>
    request<{ ok: true }>(`/v1/projects/${encodeURIComponent(slug)}/logs/sources/${id}`, { method: 'DELETE' }),
  getLogLines: (slug: string, sourceId: string, q: { from?: string | undefined; to?: string | undefined; day?: string | undefined; q?: string | undefined; limit?: number | undefined; order?: 'asc' | 'desc' | undefined } = {}) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(q)) if (v != null && v !== '') qs.set(k, String(v));
    return request<{ lines: LogLine[] }>(`/v1/projects/${encodeURIComponent(slug)}/logs/sources/${sourceId}/lines?${qs.toString()}`);
  },

  // ---- Channels (Telegram) ----
  getTelegramConfig: (slug: string) =>
    request<{ configured: boolean; enabled: boolean; botUsername: string | null }>(
      `/v1/projects/${encodeURIComponent(slug)}/channels/telegram/config`,
    ),
  saveTelegramConfig: (slug: string, input: { botToken: string; enabled?: boolean }) =>
    request<{ configured: boolean; enabled: boolean; botUsername: string | null }>(
      `/v1/projects/${encodeURIComponent(slug)}/channels/telegram/config`,
      { method: 'PUT', body: JSON.stringify(input) },
    ),
  deleteTelegramConfig: (slug: string) =>
    request<{ ok: true }>(`/v1/projects/${encodeURIComponent(slug)}/channels/telegram/config`, { method: 'DELETE' }),
  pairTelegram: (slug: string, input: { agentId?: string | null } = {}) =>
    request<{ code: string; instructions: string }>(
      `/v1/projects/${encodeURIComponent(slug)}/channels/telegram/pair`,
      { method: 'POST', body: JSON.stringify(input) },
    ),

  // ---- Documents (workspace git) ----
  listDocs: (slug: string) =>
    request<{ docs: DocEntry[] }>(`/v1/projects/${encodeURIComponent(slug)}/docs`),
  getDocsTree: (slug: string) =>
    request<{ tree: DocTreeNode[]; mode?: 'sources' | 'workspace' }>(`/v1/projects/${encodeURIComponent(slug)}/docs/tree`),
  getDoc: (slug: string, path: string) =>
    request<{ path: string; content: string }>(`${docsBase(slug)}/${encPath(path)}`),
  // URL de visualização standalone (abre em nova aba; HTML servido sandboxed).
  docViewUrl: (slug: string, path: string) =>
    `/v1/projects/${encodeURIComponent(slug)}/doc-view/${encPath(path)}`,
  saveDoc: (slug: string, path: string, content: string) =>
    request<{ ok: true; path: string }>(`${docsBase(slug)}/${encPath(path)}`, {
      method: 'PUT',
      body: JSON.stringify({ content }),
    }),
  deleteDoc: (slug: string, path: string) =>
    request<{ ok: true }>(`${docsBase(slug)}/${encPath(path)}`, { method: 'DELETE' }),
  // ---- Versionamento de docs (histórico/diff/rollback) ----
  docHistory: (slug: string, path: string) =>
    request<{ path: string; versions: DocVersion[] }>(`/v1/projects/${encodeURIComponent(slug)}/docs-history/${encPath(path)}`),
  docAt: (slug: string, path: string, sha: string) =>
    request<{ path: string; sha: string; content: string }>(`/v1/projects/${encodeURIComponent(slug)}/docs-at/${encPath(path)}?sha=${encodeURIComponent(sha)}`),
  docDiff: (slug: string, path: string, sha: string) =>
    request<{ path: string; sha: string; diff: string }>(`/v1/projects/${encodeURIComponent(slug)}/docs-at/${encPath(path)}?sha=${encodeURIComponent(sha)}&diff=1`),
  docRevert: (slug: string, path: string, sha: string) =>
    request<{ ok: true; committed: boolean; sha?: string; content: string }>(`/v1/projects/${encodeURIComponent(slug)}/docs-revert/${encPath(path)}`, {
      method: 'POST',
      body: JSON.stringify({ sha }),
    }),
  aiEditDoc: (slug: string, path: string, body: { instruction: string; content: string; selection?: { from: number; to: number; text: string } }) =>
    request<{ newContent: string; replacedSelection: boolean }>(`/v1/projects/${encodeURIComponent(slug)}/docs-ai-edit/${encPath(path)}`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  moveDoc: (slug: string, from: string, to: string) =>
    request<{ ok: true; path: string }>(`/v1/projects/${encodeURIComponent(slug)}/docs-move`, {
      method: 'POST',
      body: JSON.stringify({ from, to }),
    }),
  // ---- Publicação da base de conhecimento no repo de código (branch base) ----
  publishDocs: (slug: string) =>
    request<{ published: boolean; sha?: string; reason?: string; files?: number }>(
      `/v1/projects/${encodeURIComponent(slug)}/docs/publish`,
      { method: 'POST' },
    ),
  docsPublishStatus: (slug: string) =>
    request<{
      pending: boolean;
      scheduled: boolean;
      last: { at: string; sha?: string; files?: number } | null;
      repo: string | null;
      baseBranch: string;
    }>(`/v1/projects/${encodeURIComponent(slug)}/docs/publish-status`),

  // ---- Repos vinculados + fontes de documentação (pastas de repos) ----
  listRepos: (slug: string) =>
    request<{ repos: LinkedRepo[] }>(`/v1/projects/${encodeURIComponent(slug)}/repos`),
  linkRepo: (slug: string, body: { repoUrl: string; label?: string; defaultBranch?: string; githubToken?: string }) =>
    request<{ repo: LinkedRepo }>(`/v1/projects/${encodeURIComponent(slug)}/repos`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  unlinkRepo: (slug: string, repoId: string) =>
    request<{ ok: true }>(`/v1/projects/${encodeURIComponent(slug)}/repos/${encodeURIComponent(repoId)}`, { method: 'DELETE' }),
  repoBranches: (slug: string, repoId: string) =>
    request<{ branches: string[] }>(`/v1/projects/${encodeURIComponent(slug)}/repos/${encodeURIComponent(repoId)}/branches`),
  repoFolders: (slug: string, repoId: string, branch?: string) =>
    request<{ branch: string; folders: string[] }>(
      `/v1/projects/${encodeURIComponent(slug)}/repos/${encodeURIComponent(repoId)}/folders${branch ? `?branch=${encodeURIComponent(branch)}` : ''}`,
    ),
  listDocSources: (slug: string) =>
    request<{ sources: DocSource[] }>(`/v1/projects/${encodeURIComponent(slug)}/doc-sources`),
  addDocSource: (slug: string, body: { repoId: string; branch: string; path: string; label?: string }) =>
    request<{ source: DocSource | null; created: boolean }>(`/v1/projects/${encodeURIComponent(slug)}/doc-sources`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  removeDocSource: (slug: string, id: string) =>
    request<{ ok: true }>(`/v1/projects/${encodeURIComponent(slug)}/doc-sources/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  createDocFolder: (slug: string, path: string) =>
    request<{ ok: true }>(`${docsBase(slug)}/folder`, { method: 'POST', body: JSON.stringify({ path }) }),
  generateDocIndex: (slug: string, path: string) =>
    request<{ path: string; content: string }>(`${docsBase(slug)}/index`, {
      method: 'POST',
      body: JSON.stringify({ path }),
    }),
  importDocs: (slug: string, basePath: string, files: File[]) => {
    const form = new FormData();
    for (const f of files) {
      // webkitRelativePath preserva a estrutura de pastas no import de diretório.
      const rel = (f as File & { webkitRelativePath?: string }).webkitRelativePath || f.name;
      form.append('files', f, rel);
    }
    return request<{ ok: true; written: number; skipped: string[] }>(
      `${docsBase(slug)}/import?path=${encodeURIComponent(basePath)}`,
      { method: 'POST', body: form },
    );
  },

  // ---- Biblioteca GLOBAL de skills (workspace git próprio, fora de projeto) ----
  // Reusa os mesmos tipos de docs (DocTreeNode/DocVersion). Leitura: qualquer
  // usuário autenticado; escrita: admin (o backend gateia). Cada projeto importa
  // estas skills e melhora à sua maneira. Ver apps/api/src/routes/library.ts.
  // Lista skill-aware (padrão agentskills.io): name + description de cada skill.
  getSkills: () =>
    request<{ skills: SkillSummary[] }>(`/v1/library/skills`),
  getSkillModels: () =>
    request<{ claude: string[]; hermes: string[] }>(`/v1/library/models`),
  getSkillsTree: () =>
    request<{ tree: DocTreeNode[] }>(`/v1/library/skills/tree`),
  getSkill: (path: string) =>
    request<{ path: string; content: string }>(`/v1/library/skills/${encSkillPath(path)}`),
  saveSkill: (path: string, content: string) =>
    request<{ ok: true; path: string }>(`/v1/library/skills/${encSkillPath(path)}`, {
      method: 'PUT',
      body: JSON.stringify({ content }),
    }),
  deleteSkill: (path: string) =>
    request<{ ok: true }>(`/v1/library/skills/${encSkillPath(path)}`, { method: 'DELETE' }),
  skillHistory: (path: string) =>
    request<{ path: string; versions: DocVersion[] }>(`/v1/library/skills-history/${encSkillPath(path)}`),
  skillAt: (path: string, sha: string) =>
    request<{ path: string; sha: string; content: string }>(`/v1/library/skills-at/${encSkillPath(path)}?sha=${encodeURIComponent(sha)}`),
  skillRevert: (path: string, sha: string) =>
    request<{ ok: true; committed: boolean; sha?: string; content: string }>(`/v1/library/skills-revert/${encSkillPath(path)}`, {
      method: 'POST',
      body: JSON.stringify({ sha }),
    }),

  // Edição por IA de uma skill (reescreve doc inteiro ou só o trecho selecionado).
  aiEditSkill: (body: { instruction: string; content: string; selection?: { text: string; from?: number; to?: number } }) =>
    request<{ newContent: string; replacedSelection: boolean }>(`/v1/library/skills-ai`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // ---- Hermes (harness apartado) — status/config pra a tela "Agentes ativos" ----
  getHermesStatus: () => request<{ hermes: HermesStatus }>(`/v1/admin/hermes`),

  // ---- Conversations (monitoramento, maintainer) ----
  listConversations: (slug: string) =>
    request<{ conversations: ConversationSummary[] }>(
      `/v1/projects/${encodeURIComponent(slug)}/conversations`,
    ),
  getConversation: (slug: string, threadId: string) =>
    request<{ thread: ChatThread; messages: ChatMessage[] }>(
      `/v1/projects/${encodeURIComponent(slug)}/conversations/${threadId}`,
    ),

  // ---- Chat ----
  listThreads: () => request<{ threads: ChatThreadSummary[] }>('/v1/chat/threads'),
  createThread: (input: { title?: string; projectId?: string | null; agentId?: string | null; mode?: ChatThreadMode | null }) =>
    request<{ thread: ChatThread }>('/v1/chat/threads', { method: 'POST', body: JSON.stringify(input) }),
  getThread: (id: string) =>
    request<{ thread: ChatThread; messages: ChatMessage[]; running: boolean }>(`/v1/chat/threads/${id}`),
  renameThread: (id: string, title: string) =>
    request<{ thread: ChatThread }>(`/v1/chat/threads/${id}`, { method: 'PATCH', body: JSON.stringify({ title }) }),
  deleteThread: (id: string) => request<{ ok: true }>(`/v1/chat/threads/${id}`, { method: 'DELETE' }),
  dispatchFromThread: (threadId: string, input: { title: string; description: string; kind: 'bug' | 'suggestion'; baseBranch?: string | undefined; wantsEphemeral?: boolean | undefined }) =>
    request<{ reportId: string; report: Report }>(`/v1/chat/threads/${threadId}/dispatch`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  // ---- RBAC (admin) ----
  // ---- Roteiros/fluxos do pipeline (.md de comportamento das IAs) ----
  listPipelineMd: () =>
    request<{ items: { category: string; name: string; updatedAt: string }[] }>('/v1/admin/pipeline-md'),
  getPipelineMd: (category: string, name: string) =>
    request<{ item: { category: string; name: string; content: string; updatedAt: string } }>(
      `/v1/admin/pipeline-md/${encodeURIComponent(category)}/${encodeURIComponent(name)}`,
    ),
  savePipelineMd: (category: string, name: string, content: string) =>
    request<{ ok: true }>(`/v1/admin/pipeline-md/${encodeURIComponent(category)}/${encodeURIComponent(name)}`, {
      method: 'PUT',
      body: JSON.stringify({ content }),
    }),
  listRoles: () =>
    request<{ permissions: PermissionDef[]; roles: RoleDef[]; rolePermissions: Record<string, string[]> }>('/v1/admin/roles'),
  setRolePermissions: (key: string, permissions: string[]) =>
    request<{ ok: true; roleKey: string; permissions: string[] }>(`/v1/admin/roles/${encodeURIComponent(key)}/permissions`, {
      method: 'PUT',
      body: JSON.stringify({ permissions }),
    }),
  listMembers: (slug: string) =>
    request<{ members: ProjectMemberRow[] }>(`/v1/projects/${encodeURIComponent(slug)}/rbac-members`),
  setMemberRole: (slug: string, userId: string, roleKey: string) =>
    request<{ ok: true; roleKey: string }>(`/v1/projects/${encodeURIComponent(slug)}/rbac-members/${userId}`, {
      method: 'PUT',
      body: JSON.stringify({ roleKey }),
    }),
};

export interface PermissionDef { key: string; area: string; label: string }
export interface RoleDef { key: string; label: string; description: string | null; builtin: boolean }
export interface ProjectMemberRow { userId: string; email: string; displayName: string; globalRole: string; roleKey: string | null; role: string }

// ---- Environments types ----

export type EnvironmentKind = 'vps' | 'local';

export interface ProjectEnvironment {
  id: string;
  projectId: string;
  name: string;
  kind: EnvironmentKind;
  url: string | null;
  host: string | null;
  sshUser: string | null;
  sshPort: number | null;
  sshKeyPath: string | null;
  composeFile: string | null;
  composeProject: string | null;
  notes: string | null;
  tier: string;
  dbContainer: string | null;
  dbName: string | null;
  dbRoleRead: string | null;
  dbRoleWrite: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AgentDbGrant {
  agentId: string;
  environmentId: string;
  operations: string[];
  tables: string[];
  maxRows: number;
  requireConfirmWrite: boolean;
  instructions: string | null;
  enabled: boolean;
}

export type DbQueryResult =
  | { ok: true; output: string; operation: string; rowCount?: number; isWrite: boolean }
  | { ok: false; error: string; needsConfirm?: boolean; operation?: string; isWrite?: boolean };

export interface DbAuditRow {
  id: string;
  agentSlug: string | null;
  envName: string | null;
  tier: string | null;
  operation: string | null;
  tables: string[];
  sql: string;
  allowed: boolean;
  reason: string | null;
  rowCount: number | null;
  createdAt: string;
}

export interface EnvironmentInput {
  name: string;
  kind: EnvironmentKind;
  url?: string;
  host?: string;
  sshUser?: string;
  sshPort?: number;
  sshKeyPath?: string;
  composeFile?: string;
  composeProject?: string;
  notes?: string;
  tier?: string;
  dbContainer?: string;
  dbName?: string;
  dbRoleRead?: string;
  dbRoleWrite?: string;
  active?: boolean;
}

export interface EnvironmentSnapshot {
  ranAt: string;
  durationMs: number;
  ok: boolean;
  error?: string;
  raw?: string;
  host: {
    kernel: string | null;
    hostname: string | null;
    uptime: string | null;
    loadavg: string | null;
    cpu: { model: string | null; cores: number | null } | null;
  };
  memory: {
    totalMb: number;
    usedMb: number;
    freeMb: number;
    availableMb: number | null;
  } | null;
  disks: Array<{
    filesystem: string;
    size: string;
    used: string;
    avail: string;
    usePct: string;
    mount: string;
  }>;
  docker: { version: string | null; installed: boolean };
  containers: Array<{
    name: string;
    image: string;
    status: string;
    ports: string;
    cpuPerc: string | null;
    memUsage: string | null;
    memPerc: string | null;
    netIO: string | null;
    blockIO: string | null;
  }>;
  compose: {
    file: string | null;
    services: Array<{ name: string; service: string; state: string; status: string }>;
  };
}

// ---- Agent types ----

export interface ClaudeModel {
  id: string;
  label: string;
  tier: string;
}

export type AgentScope = 'docs' | 'code';
export type PipelineFlow = 'suggestion' | 'bug' | 'feature';
export type PipelineRole =
  | 'product-analyst' | 'dev-front' | 'dev-back'
  | 'test-reviewer' | 'security-reviewer' | 'practices-reviewer' | 'business-reviewer';

export interface Agent {
  id: string;
  projectId: string;
  slug: string;
  name: string;
  description: string;
  scope: AgentScope;
  pipelineRole: PipelineRole | null;
  avatarKey: string | null;
  greeting: string | null;
  accentColor: string | null;
  soulPath: string;
  isRouter: boolean;
  enabled: boolean;
  seed: boolean;
  model: string | null;
  engine: 'claude' | 'hermes';
  createdAt: string;
  updatedAt: string;
}

export interface AgentInput {
  slug: string;
  name: string;
  description?: string;
  scope?: AgentScope;
  model?: string;
  engine?: 'claude' | 'hermes';
}

export type AgentPatch = Partial<Omit<AgentInput, 'slug' | 'model'>> & {
  model?: string | null;
  soul?: string;
  enabled?: boolean;
  greeting?: string | null;
  accentColor?: string | null;
  /** Globs que o agente pode escrever (ex: ['docs/notas/**', 'docs/cards-beta/**']). */
  writes?: string[];
};

export interface DocEntry {
  /** Caminho relativo ao root do workspace, ex: 'docs/posicionamento.md'. */
  path: string;
  size: number;
  updatedAt: string | null;
}

export interface DocTreeNode {
  name: string;
  /** Caminho relativo ao root do workspace, ex: 'docs/marca/tom.md'. */
  path: string;
  type: 'dir' | 'file';
  size?: number;
  children?: DocTreeNode[];
}

export interface DocVersion {
  sha: string;
  author: string;
  email: string;
  date: string;
  message: string;
}

/** Repo vinculado a um projeto (pode ter vários). */
export interface LinkedRepo {
  id: string;
  repoUrl: string;
  label: string;
  defaultBranch: string;
  hasToken: boolean;
}

/** Imagem anexada a uma mensagem de chat (referência no storage). */
export interface ChatImageRef {
  key: string;
  name: string;
  contentType: string;
}

/** Fonte da base: uma pasta de um repo vinculado, mostrada em Documentos. */
export interface DocSource {
  id: string;
  repoId: string;
  repoLabel: string;
  repoUrl: string;
  branch: string;
  path: string;
  label: string;
  kind: string;
}

/** Status/config do harness Hermes (apartado), pra a tela "Agentes ativos". */
export interface HermesStatus {
  enabled: boolean;
  url: string | null;
  model: string;
  routerUsesHermes: boolean;
  health: 'online' | 'offline' | 'unauthorized' | 'disabled';
  models: string[];
}

/** Resumo de uma skill no padrão agentskills.io (pasta com SKILL.md). */
export interface SkillSummary {
  /** name do frontmatter (== nome da pasta). */
  name: string;
  /** description do frontmatter (o que faz e quando usar). */
  description: string;
  /** Nome da pasta da skill. */
  dir: string;
  /** Caminho do SKILL.md (pra abrir/editar via os métodos getSkill/saveSkill). */
  path: string;
  /** Origem: 'default' (veio com o Hermes), 'mine' (criada via bug-pilot) ou
   * 'hermes-run' (gerada pelo Hermes em execução, ainda não versionada). */
  origin: 'default' | 'mine' | 'hermes-run';
}

// ---- Logs de aplicações ----

export interface LogSource {
  id: string;
  projectId: string;
  name: string;
  containerName: string;
  sshHost: string;
  sshUser: string;
  sshKeyPath: string;
  enabled: boolean;
  ttlHours: number | null;
  lastCollectedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface LogSourceInput {
  name: string;
  containerName: string;
  sshHost?: string;
  sshUser?: string;
  sshKeyPath?: string;
  ttlHours?: number | null;
  enabled?: boolean;
}

export interface LogLine {
  id: number;
  ts: string;
  message: string;
}

// ---- Ambientes efêmeros ----

export type EphemeralStatus = 'creating' | 'running' | 'stopping' | 'destroyed' | 'error';

export interface EphemeralEnv {
  id: string;
  projectId: string;
  reportId: string | null;
  branch: string;
  name: string;
  mode: 'auto' | 'manual';
  status: EphemeralStatus;
  url: string | null;
  composeProject: string;
  hostPort: number | null;
  sshHost: string;
  workdir: string;
  lastError: string | null;
  ttlAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface EphemeralConfig {
  projectId: string;
  enabled: boolean;
  sshHost: string;
  sshUser: string;
  sshKeyPath: string;
  repoUrl: string | null;
  composeFile: string;
  workdir: string;
  baseDomain: string;
  appPort: number;
  portBase: number;
  migrateCmd: string | null;
  seedCmd: string | null;
  defaultTtlMinutes: number;
  // Modo subdomínio HTTPS (Traefik)
  composeTemplate: string | null;
  proxyService: string | null;
  proxyPort: number;
  proxyNetwork: string;
  traefikDynamicDir: string;
  preUpCmd: string | null;
  dbService: string | null;
  dbCopyCmd: string | null;
}

export type EphemeralConfigInput = Omit<EphemeralConfig, 'projectId'>;

// ---- Chat types ----

export type ChatRole = 'user' | 'assistant' | 'system' | 'tool';
export type ChatThreadMode = 'direct' | 'router';

export interface ChatThread {
  id: string;
  userId: string;
  projectId: string | null;
  agentId: string | null;
  mode: ChatThreadMode | null;
  title: string;
  claudeSessionId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ChatThreadSummary {
  id: string;
  title: string;
  projectId: string | null;
  projectSlug: string | null;
  projectName: string | null;
  agentId: string | null;
  agentName: string | null;
  mode: ChatThreadMode | null;
  createdAt: string;
  updatedAt: string;
}

export interface ChatMessage {
  id: string;
  threadId: string;
  role: ChatRole;
  content: string;
  toolInfo: Record<string, unknown> | null;
  /** 'streaming' | 'complete' | 'error' | null (legado = completa). */
  status?: string | null;
  createdAt: string;
}

export interface ConversationSummary {
  id: string;
  title: string;
  mode: ChatThreadMode | null;
  channelThreadId: string | null;
  channel: 'web' | 'telegram';
  agentName: string | null;
  userName: string | null;
  createdAt: string;
  updatedAt: string;
  messageCount: number;
}

// ---- Types ----

export type GlobalRole = 'admin' | 'user';
export type ProjectRole = 'maintainer' | 'viewer';
export type ReportStatus =
  | 'queued' | 'triaging' | 'dispatched' | 'committed' | 'pr_open'
  | 'deploying' | 'in_review' | 'resolved' | 'rejected';
export type ReportKind = 'bug' | 'suggestion' | 'scenario';

export type StepClause = 'given' | 'when' | 'then';
export type StepPatternKind = 'expression' | 'regex';

export interface StepLibraryEntry {
  id: string;
  projectId: string;
  clause: StepClause;
  pattern: string;
  patternKind: StepPatternKind;
  argTypes: string[];
  sourceFile: string;
  sourceLine: number | null;
  firstSeenInReportId: string | null;
  deprecated: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Me {
  id: string;
  email: string;
  displayName: string;
  globalRole: GlobalRole;
}

export interface AccessTokenInfo {
  id: string;
  label: string;
  prefix: string;
  createdAt: string;
  lastUsedAt?: string | null;
  expiresAt?: string | null;
}

export interface SyncAppFile {
  name: string;
  size: number;
  platform: string;
  url: string;
}

export interface DeviceLookupInfo {
  userCode: string;
  label: string;
  createdAt: string;
  expired: boolean;
  status: string;
}

export interface AdminRun {
  id: string;
  kind: string;
  projectId?: string;
  projectSlug?: string;
  agentSlug?: string;
  threadId?: string;
  actor?: string;
  model?: string;
  startedAt: number;
}

export interface RunSlots {
  active: number;
  max: number;
  waiting: number;
}

export interface WarmSession {
  id: string;
  title: string;
  projectSlug: string | null;
  agentName: string | null;
  channelThreadId: string | null;
  updatedAt: string;
}

export interface AdminUser {
  id: string;
  email: string;
  displayName: string;
  globalRole: GlobalRole;
  active: boolean;
  createdAt: string;
  lastLoginAt: string | null;
}

export interface Project {
  id: string;
  slug: string;
  name: string;
  repoUrl: string;
  baseBranch: string;
  localRepoPath: string | null;
  premisesMd: string;
  configYaml: string;
  active: boolean;
  githubToken: string | null;   // mascarado no listing, só o dono vê
  githubActor: string | null;
  // BDD config
  bddFramework: string;
  featuresDir: string;
  stepsDir: string;
  testCommand: string;
  testBaseUrl: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ProjectListItem extends Project {
  role?: ProjectRole;
}

export interface NewProjectInput {
  slug: string;
  name: string;
  repoUrl: string;
  baseBranch?: string;
  localRepoPath?: string;
  premisesMd?: string;
  configYaml?: string;
  githubToken?: string;
  githubActor?: string;
  bddFramework?: string;
  featuresDir?: string;
  stepsDir?: string;
  testCommand?: string;
  testBaseUrl?: string;
}

export type ProjectPatch = Partial<Omit<NewProjectInput, 'slug'>>;

export interface Report {
  id: string;
  projectId: string;
  kind: ReportKind;
  status: ReportStatus;
  title: string;
  description: string;
  reporterContact: string | null;
  pageUrl: string | null;
  releaseVersion: string | null;
  userAgent: string | null;
  sessionArtifactKey: string | null;
  consoleArtifactKey: string | null;
  triageVerdict: string | null;
  triageNotes: string | null;
  prUrl: string | null;
  prNumber: number | null;
  branchName: string | null;
  openPrRequested?: boolean;
  ciStatus: 'pending' | 'passed' | 'failed' | null;
  ciRunUrl: string | null;
  autoFixAttempts: number;
  deployStatus: 'queued' | 'running' | 'success' | 'failure' | null;
  deployRunId: string | null;
  deployRunUrl: string | null;
  deployTarget: 'dev' | 'prod' | null;
  lastTesterNote: string | null;
  createdAt: string;
  updatedAt: string;
}

export type TestReportStatus = 'passed' | 'failed' | 'broken';
export type TestTrigger = 'ci' | 'manual' | 'scheduled';

export interface TestReport {
  id: string;
  projectId: string;
  branch: string;
  sha: string;
  runId: string;
  trigger: TestTrigger;
  status: TestReportStatus;
  passedCount: number;
  failedCount: number;
  brokenCount: number;
  skippedCount: number;
  durationMs: number;
  prNumber: number | null;
  storagePath: string;
  ciRunUrl: string | null;
  reportUrl: string;
  createdAt: string;
}

export interface ReportEvent {
  id: string;
  reportId: string;
  type: string;
  actorUserId: string | null;
  actorLabel: string | null;
  payload: Record<string, unknown>;
  createdAt: string;
}

// ---- Local features (read from project's localRepoPath) ----
export interface LocalStepInfo {
  /** Original keyword preserved (Given/Dado/E/And/etc). */
  keyword: string;
  text: string;
  line: number;
}
export interface LocalScenarioInfo {
  name: string;
  line: number;
  tags: string[];
  outline: boolean;
  steps: LocalStepInfo[];
}
export interface LocalFeatureInfo {
  /** Forward-slashed path relative to localRepoPath. */
  path: string;
  name: string;
  description: string;
  tags: string[];
  scenarios: LocalScenarioInfo[];
}
export interface LocalFeaturesResponse {
  filesScanned: number;
  features: LocalFeatureInfo[];
}

// ---- Local Allure import ----
export interface AllureImportSummary {
  passedCount: number;
  failedCount: number;
  brokenCount: number;
  skippedCount: number;
  durationMs: number;
  total: number;
  latestStop: number | null;
  earliestStart: number | null;
  features: string[];
}
export interface ImportLocalTestReportResult {
  id: string;
  branch: string;
  runId: string;
  status: 'passed' | 'failed' | 'broken';
  summary: AllureImportSummary;
  candidates: string[];
  usedDir: string;
  htmlGenerated: boolean;
  generateError: string | null;
  reportUrl: string | null;
}
